import { ICustomerRepository } from '$src/domain/customers/ICustomerRepository';
import { ProjectSummary } from '$src/domain/projects/ProjectSummary';
import { injectable } from 'tsyringe';
import { ApiRoutes } from '../apiConfig/ApiRoutes';
import { BaseApi } from '../apiConfig/BaseApi';
import { plainToInstance } from 'class-transformer';
import { PagedResult } from '$src/domain/core/PagedResult';
import { CustomerSummary } from '$src/domain/customers/CustomerSummary';
import { Customer } from '$src/domain/customers/Customer';
import { Folder } from '$src/domain/customers/Folder';
import { CustomerType } from '$src/domain/customers/CustomerType';
import { CreateCustomerData } from '$src/domain/customers/CreateCustomerData';
import { CustomerIdentifier } from '$src/domain/customers/CustomerIdentifier';
import { ProjectType } from '$src/domain/customers/ProjectType';

@injectable()
export class CustomerRepositoryApi extends BaseApi implements ICustomerRepository {
  
    
    private static ROUTES = 
    {
        RecentProjects: 'recent-projects',
        CustomerProjects: 'projects',
        Folders: 'folders',
        Types: 'types',
        ProjectTypes: 'project-types'
    };

    protected static getRoute(groupStoreId: string, path1: string, path2?: string): string {
        if (path2) {
            return `${ApiRoutes.CUSTOMERS.endpoint(groupStoreId)}/${path1}/${path2}`;
        } else {
            return `${ApiRoutes.CUSTOMERS.endpoint(groupStoreId)}/${path1}`;
        }
    }

    async getCustomerById(groupStoreId: string, customerId: string): Promise<Customer> {
        try {

            const route = CustomerRepositoryApi.getRoute(groupStoreId, customerId);

            const response = await this.apiClient.get<Customer>(route);
            
            return response;
            // plainToInstance(Customer, response);
        } catch (error) {
            console.error('Error fetching customer by id:', error);
            throw error; 
        }
    }
    
    async getCustomersByRecentProjects(groupStoreId: string, pattern: string, pageNumber: number, pageSize: number): Promise<PagedResult<CustomerSummary>> {
        try {

            const route = CustomerRepositoryApi.getRoute(groupStoreId, CustomerRepositoryApi.ROUTES.RecentProjects);

            const response = await this.apiClient.get<PagedResult<CustomerSummary>>(route, 
            {
                search: pattern,
                pageNumber: pageNumber,
                pageSize: pageSize,
            })
            
            return response;

        } catch (error) {
            console.error('Error fetching customers:', error);
            throw error; 
        }
    }

    async getProjectsByCustomerId(groupStoreId: string, customerId: string, pageNumber: number, pageSize: number): Promise<PagedResult<ProjectSummary>> {
        try {

            const route = CustomerRepositoryApi.getRoute(groupStoreId, customerId, CustomerRepositoryApi.ROUTES.CustomerProjects);

            const response = await this.apiClient.get<PagedResult<ProjectSummary>>(route, 
            {
                pageNumber: pageNumber,
                pageSize: pageSize,
            })
            
            return plainToInstance(PagedResult<ProjectSummary>, response,  {
                targetMaps: 
                [
                    {target: PagedResult, properties: { items: ProjectSummary }}
                ]
            });
        } catch (error) {
            console.error('Error fetching customers:', error);
            throw error; 
        }
    }

    async search(groupStoreId: number, pattern: string, pageNumber: number, pageSize: number): Promise<PagedResult<CustomerSummary>> {
        try {

            const route = CustomerRepositoryApi.getRoute(groupStoreId.toString(), 'search');

            const response = await this.apiClient.get<PagedResult<CustomerSummary>>(route, 
            {
                pattern: pattern,
                pageNumber: pageNumber,
                pageSize: pageSize,
            })
            
            return response;
        } catch (error) {
            console.error('Error fetching search customers:', error);
            throw error; 
        }
    }

    async getFolders(groupStoreId: number, customerId: number): Promise<Folder[]> {
        try {

            const route = CustomerRepositoryApi.getRoute(groupStoreId.toString(), customerId.toString(), CustomerRepositoryApi.ROUTES.Folders);

            const response = await this.apiClient.get<Folder[]>(route, null);
            
            return response;

        } catch (error) {
            console.error('Error fetching folders:', error);
            throw error; 
        }
    }

    async getProjectTypes(groupStoreId: number): Promise<ProjectType[]> {
        try {

            const route = CustomerRepositoryApi.getRoute(groupStoreId.toString(), CustomerRepositoryApi.ROUTES.ProjectTypes);

            const response = await this.apiClient.get<ProjectType[]>(route, null);
            
            return response;

        } catch (error) {
            console.error('Error fetching project types:', error);
            throw error; 
        }
    }
   
    async getCustomerTypes(groupStoreId: number): Promise<CustomerType[]> {
        try {

            const route = CustomerRepositoryApi.getRoute(groupStoreId.toString(), CustomerRepositoryApi.ROUTES.Types);

            const response = await this.apiClient.get<CustomerType[]>(route, null);
            
            return response;

        } catch (error) {
            console.error('Error fetching customer types:', error);
            throw error; 
        }
    }

    async createCustomer(groupStoreId: number, customer: CreateCustomerData): Promise<CustomerIdentifier> {
        try {

            const route = CustomerRepositoryApi.getRoute(groupStoreId.toString(), '');

            const response = await this.apiClient.post<CustomerIdentifier>(route, customer);
            
            return response;

        } catch (error) {
            console.error('Error creating customer:', error);
            throw error; 
        }
    }

    async updateCustomer(groupStoreId: number, customer: Customer): Promise<CustomerIdentifier> {
        try {

            const route = CustomerRepositoryApi.getRoute(groupStoreId.toString(), '');

            const response = await this.apiClient.put<CustomerIdentifier>(route, customer);
            
            return response;

        } catch (error) {
            console.error('Error updating customer:', error);
            throw error; 
        }
    }

    async hasInvoices(groupStoreId: number, customerId: number): Promise<boolean> {
        try {

            const route = CustomerRepositoryApi.getRoute(groupStoreId.toString(), customerId.toString(), 'invoices/existence');

            const response = await this.apiClient.get<boolean>(route, null);
            
            return response;

        } catch (error) {
            console.error('Error checking customer invoices:', error);
            throw error; 
        }
    }
  
}
